// Small typed client for the astrologia Mini App. All requests carry the
// Telegram initData header for server-side authentication.

function getInitData(): string {
  const tg = window.Telegram?.WebApp;
  return tg?.initData ?? '';
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      'x-telegram-init-data': getInitData(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export interface City {
  name: string;
  asciiname: string;
  country: string;
  lat: number;
  lng: number;
  population: number;
  tz: string;
}

export function searchCities(q: string): Promise<{ cities: City[] }> {
  return call(`/api/city-search?q=${encodeURIComponent(q)}&limit=8`);
}

export interface NatalRequest {
  birthDate: string;
  birthTime: string | null;
  latitude: number;
  longitude: number;
  timezone: string;
  cityName?: string;
}

export interface PlanetPosition {
  planet: string;
  longitude: number;
  latitude: number;
  sign: string;
  degreeInSign: number;
  retrograde: boolean;
  house: number | null;
}

export interface HouseCusp {
  house: number;
  longitude: number;
  sign: string;
  degreeInSign: number;
}

export interface NatalChart {
  sunSign: string;
  moonSign: string;
  ascendantSign: string | null;
  planets: Record<string, PlanetPosition>;
  houses: {
    ascendant: number;
    mc: number;
    cusps: HouseCusp[];
    fallbackEqualHouse: boolean;
  } | null;
  aspects: Array<{
    a: string;
    b: string;
    type: string;
    orb: number;
  }>;
  solarChartFallback: boolean;
}

export function computeNatal(req: NatalRequest): Promise<{ chart: NatalChart; lang: string }> {
  return call('/api/natal', { method: 'POST', body: JSON.stringify(req) });
}

export interface DailyResponse {
  sign: string;
  signName: string;
  reading: string;
  date: string;
  lang: string;
}

export function fetchDaily(): Promise<DailyResponse> {
  return call('/api/daily');
}

export interface ContentBundle {
  lang: string;
  signs: Record<string, { name: string; profile: string; keywords: string[]; element: string; modality: string; ruler: string }>;
  planets: Record<string, { name: string; archetype: string; short: string }>;
  houses: Record<string, { name: string; keywords: string[]; short: string }>;
  aspects: Record<string, { name: string; nature: string; short: string }>;
  strings: Record<string, string>;
}

export function fetchContent(): Promise<ContentBundle> {
  return call('/api/content');
}

// ── Shop + premium content ────────────────────────────────────────────────

export interface ProductView {
  id: string;
  priceStars: number;
  recurring: boolean;
  emoji: string;
  title: string;
  description: string;
  owned: boolean;
}

export function fetchProducts(): Promise<{ products: ProductView[]; lang: string }> {
  return call('/api/products');
}

export function createInvoice(productId: string): Promise<{ invoiceLink: string }> {
  return call('/api/invoice', { method: 'POST', body: JSON.stringify({ productId }) });
}

export interface PaywallError {
  error: 'locked';
  productId: string;
}

export async function fetchLoveReading(): Promise<{ ok: true; data: LoveReading } | { ok: false; locked: PaywallError }> {
  return safeCall<LoveReading>('/api/love');
}
export async function fetchDailyExtended(): Promise<{ ok: true; data: DailyExtended } | { ok: false; locked: PaywallError }> {
  return safeCall<DailyExtended>('/api/daily-extended');
}
export async function fetchWeekly(): Promise<{ ok: true; data: WeeklyReading } | { ok: false; locked: PaywallError }> {
  return safeCall<WeeklyReading>('/api/weekly');
}
export async function fetchYearAhead(): Promise<{ ok: true; data: YearAhead } | { ok: false; locked: PaywallError }> {
  return safeCall<YearAhead>('/api/year-ahead');
}
export async function fetchMoonRitual(): Promise<{ ok: true; data: MoonRitualPayload } | { ok: false; locked: PaywallError }> {
  return safeCall<MoonRitualPayload>('/api/moon-ritual');
}
export async function fetchNatalInterpretation(): Promise<{ ok: true; data: NatalInterpretation } | { ok: false; locked: PaywallError }> {
  return safeCall<NatalInterpretation>('/api/natal-interpretation');
}

export interface LoveReading { sign: string; signName: string; date: string; reading: string; lang: string }
export interface DailyExtended {
  sign: string; signName: string; date: string;
  sections: { love: string; career: string; inner: string };
  lang: string;
}
export interface WeeklyReading { sign: string; signName: string; week: string; reading: string; lang: string }
export interface YearAhead { sign: string; signName: string; year: number; reading: string; lang: string }
export interface MoonRitualPayload { phase: string; entry: { name: string; short: string; ritual: string }; lang: string }
export interface NatalInterpretation {
  sunSign: string; moonSign: string; ascendantSign: string | null;
  sections: Array<{ title: string; body: string }>;
  aspects: string[];
  lang: string;
}

async function safeCall<T>(path: string): Promise<{ ok: true; data: T } | { ok: false; locked: PaywallError }> {
  const res = await fetch(path, {
    headers: {
      'content-type': 'application/json',
      'x-telegram-init-data': getInitData(),
    },
  });
  if (res.status === 402) {
    const body = (await res.json()) as PaywallError;
    return { ok: false, locked: body };
  }
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return { ok: true, data: (await res.json()) as T };
}
